import { lazy } from "react";
import { Navigate } from "react-router-dom";

import { ACCESS_RIGHTS } from "../constants/accessRights";

// =====================================================
// #region AUTHENTICATION INNER PAGES
// =====================================================
const ResetPassword = lazy(() => import("../pages/Authentication/ResetPassword"));
const Alt404 = lazy(() => import("../pages/AuthenticationInner/Errors/Alt404"));
const Basic404 = lazy(() => import("../pages/AuthenticationInner/Errors/Basic404"));
const Error500 = lazy(() => import("../pages/AuthenticationInner/Errors/Error500"));
const LockScreen = lazy(() => import("../pages/Authentication/LockScreen"));
const Offlinepage = lazy(() => import("../pages/AuthenticationInner/Errors/Offlinepage"));
const ExpiredToken = lazy(() => import("../pages/Authentication/ExpiredToken"));
// #endregion

// =====================================================
// #region GENERAL
// =====================================================
const ComingSoon = lazy(() => import("../pages/Others/ComingSoon/ComingSoon"));
const Maintenance = lazy(() => import("../pages/Others/Maintenance/Maintenance"));
const NotFound = lazy(() => import("../pages/Others/NotFound/NotFound"));
// #endregion

// =====================================================
// #region AUTHENTICATION
// =====================================================
const Login = lazy(() => import("../pages/Authentication/Login"));
const AuthPage_LRA = lazy(() => import("../pages/Authentication/AuthPage_LRA"));
const Logout = lazy(() => import("../pages/Authentication/Logout"));
const ForgetPassword = lazy(() => import("../pages/Authentication/ForgetPassword"));
const Register = lazy(() => import("../pages/Authentication/Register"));
// #endregion

// =====================================================
// #region CORE
// =====================================================
const Notifications = lazy(() => import("../pages/Core/Notification/index"));
const Security_AuditLogs = lazy(() => import("../pages/Security/AuditLogs/index"));
const Security_Roles = lazy(() => import("../pages/Security/Roles/index"));
const Security_Users = lazy(() => import("../pages/Security/Users/index"));
const Security_AccessRequests = lazy(() => import("../pages/Security/AccessRequests/index"));
// #endregion

// =====================================================
// #region FMS
// =====================================================
const FMS_Dashboards_Accounting = lazy(() => import("../pages/FMS/Dashboards/Accounting/index"));
const FMS_Dashboards_Budget = lazy(() => import("../pages/FMS/Dashboards/Budget/index"));
const FMS_Dashboards_Collection = lazy(() => import("../pages/FMS/Dashboards/Collection/index"));
const FMS_Dashboards_Disbursement = lazy(() => import("../pages/FMS/Dashboards/Disbursement/index"));

const FMS_Settings = lazy(() => import("../pages/FMS/StaticData"));
const FMS_Settings_AccountableForms = lazy(() => import("../pages/FMS/StaticData/AccountableForms"));
const FMS_Settings_AllotmentSourceDocumentTypes = lazy(() => import("../pages/FMS/StaticData/AllotmentSourceDocumentTypes"));
const FMS_Settings_BankDetails = lazy(() => import("../pages/FMS/StaticData/BankDetails"));
const FMS_Settings_Clients = lazy(() => import("../pages/FMS/StaticData/Clients"));
const FMS_Settings_DocumentTypes = lazy(() => import("../pages/FMS/StaticData/DocumentTypes"));
const FMS_Settings_FundSources = lazy(() => import("../pages/FMS/StaticData/FundSources"));
const FMS_Settings_ChartOfAccounts = lazy(() => import("../pages/FMS/StaticData/ChartOfAccounts/index"));
const FMS_Settings_ObjectCodes = lazy(() => import("../pages/FMS/StaticData/ObjectCodes/index"));
const FMS_Settings_Offices = lazy(() => import("../pages/FMS/StaticData/Offices/index"));
const FMS_Settings_Programs = lazy(() => import("../pages/FMS/StaticData/Programs/index"));
const FMS_Settings_TransactionTypes = lazy(() => import("../pages/FMS/StaticData/TransactionTypes/index"));

const FMS_Accounting_DepreciationSchedules = lazy(() => import("../pages/FMS/Accounting/Depreciation/index"));
const FMS_Accounting_TransactionInboxes = lazy(() => import("../pages/FMS/Accounting/TransactionInboxes/index"));

const FMS_Budget_GAA = lazy(() => import("../pages/FMS/Budget/GAA/index"));
const FMS_Budget_BUR = lazy(() => import("../pages/FMS/Budget/BUR/index"));
const FMS_Budget_SARO = lazy(() => import("../pages/FMS/Budget/SARO/index"));

const FMS_Transactions_Allotments = lazy(() => import("../pages/FMS/Transactions/Allotments/index"));
const FMS_Transactions_Appropriations = lazy(() => import("../pages/FMS/Transactions/Appropriations/index"));
const FMS_Transactions_CashDisbursements = lazy(() => import("../pages/FMS/Transactions/CashDisbursements/index"));
const FMS_Transactions_CheckDisbursements = lazy(() => import("../pages/FMS/Transactions/CheckDisbursements/index"));
const FMS_Transactions_Collections = lazy(() => import("../pages/FMS/Transactions/Collections/index"));
const FMS_Transactions_Deposits = lazy(() => import("../pages/FMS/Transactions/Deposits/index"));
const FMS_Transactions_DisbursementVouchers = lazy(() => import("../pages/FMS/Transactions/DisbursementVouchers/index"));
const FMS_Transactions_JEV = lazy(() => import("../pages/FMS/Transactions/JournalEntryVouchers"));
const FMS_Transactions_Obligations = lazy(() => import("../pages/FMS/Transactions/Obligations/index"));
const FMS_Transactions_OrderOfPayments = lazy(() => import("../pages/FMS/Transactions/OrderOfPayments/index"));
const FMS_Transactions_Prepayments = lazy(() => import("../pages/FMS/Transactions/Prepayments/index"));

const FMS_Reports_Accounting = lazy(() => import("../pages/FMS/Reports/Accounting/index"));
const FMS_Reports_Budget = lazy(() => import("../pages/FMS/Reports/Budget/index"));
const FMS_Reports_Collection = lazy(() => import("../pages/FMS/Reports/Collection/index"));
const FMS_Reports_Disbursement = lazy(() => import("../pages/FMS/Reports/Disbursement/index"));
// #endregion

// =====================================================
// #region SOCIALS
// =====================================================
const Socials_FAQ = lazy(() => import("../pages/Socials/FAQ/index"));
const Socials_PrivacyPolicy = lazy(() => import("../pages/Socials/PrivacyPolicy/index"));
const Socials_TermsAndConditions = lazy(() => import("../pages/Socials/TermsAndConditions/index"));
// #endregion

const authProtectedRoutes = [
  { path: "/index", component: <FMS_Dashboards_Accounting /> },

  // =====================================================
  // #region CORE
  // =====================================================
  {
    path: "/notifications",
    component: <Notifications />,
    permissionTypeId: [],
  },

  {
    path: "/security/users",
    component: <Security_Users />,
    permissionTypeId: [ACCESS_RIGHTS.CORE_SECURITY_USERS],
  },
  {
    path: "/security/roles",
    component: <Security_Roles />,
    permissionTypeId: [ACCESS_RIGHTS.CORE_SECURITY_ROLES],
  },
  {
    path: "/security/auditLogs",
    component: <Security_AuditLogs />,
    permissionTypeId: [ACCESS_RIGHTS.CORE_SECURITY_AUDITLOGS],
  },
  {
    path: "/security/accessrequests",
    component: <Security_AccessRequests />,
    permissionTypeId: [
      ACCESS_RIGHTS.CORE_TRANSACTIONS_ACCESSREQUESTS_VIEWER,
      ACCESS_RIGHTS.CORE_TRANSACTIONS_ACCESSREQUESTS_REQUESTOR,
      ACCESS_RIGHTS.CORE_TRANSACTIONS_ACCESSREQUESTS_APPROVER,
    ]
  },
  // #endregion

  // =====================================================
  // #region FMS
  // =====================================================
  {
    path: "/fms/dashboards/accounting",
    component: <FMS_Dashboards_Accounting />,
    permissionTypeId: [ACCESS_RIGHTS.FMS_DASHBOARD_ACCOUNTING],
  },
  {
    path: "/fms/dashboards/budget",
    component: <FMS_Dashboards_Budget />,
    permissionTypeId: [ACCESS_RIGHTS.FMS_DASHBOARD_BUDGET],
  },
  {
    path: "/fms/dashboards/collection",
    component: <FMS_Dashboards_Collection />,
    permissionTypeId: [ACCESS_RIGHTS.FMS_DASHBOARD_COLLECTION],
  },
  {
    path: "/fms/dashboards/disbursement",
    component: <FMS_Dashboards_Disbursement />,
    permissionTypeId: [ACCESS_RIGHTS.FMS_DASHBOARD_DISBURSEMENT],
  },
  {
    path: "/fms/settings",
    component: <FMS_Settings />,
    permissionTypeId: [
      ACCESS_RIGHTS.FMS_STATICDATA_ACCOUNTABLEFORMS,
      ACCESS_RIGHTS.FMS_STATICDATA_ACCOUNTCLASSIFICATIONS,
      ACCESS_RIGHTS.FMS_STATICDATA_ALLOTMENTSOURCEDOCUMENTTYPES,
      ACCESS_RIGHTS.FMS_STATICDATA_BANKS,
      ACCESS_RIGHTS.FMS_STATICDATA_BANKBRANCHES,
      ACCESS_RIGHTS.FMS_STATICDATA_BANKACCOUNTS,
      ACCESS_RIGHTS.FMS_STATICDATA_CLIENTS,
      ACCESS_RIGHTS.FMS_STATICDATA_DOCUMENTTYPES,
      ACCESS_RIGHTS.FMS_STATICDATA_FINANCINGSOURCES,
      ACCESS_RIGHTS.FMS_STATICDATA_FUNDCATEGORIES,
      ACCESS_RIGHTS.FMS_STATICDATA_FUNDCLUSTERS,
      ACCESS_RIGHTS.FMS_STATICDATA_GENERALLEDGERS,
      ACCESS_RIGHTS.FMS_STATICDATA_SUBSIDIARYLEDGERS,
      ACCESS_RIGHTS.FMS_STATICDATA_TRANSACTIONTYPES,
    ],
  },
  {
    path: "/fms/settings/accountableforms",
    component: <FMS_Settings_AccountableForms />,
    permissionTypeId: [ACCESS_RIGHTS.FMS_STATICDATA_ACCOUNTABLEFORMS],
  },
  {
    path: "/fms/settings/allotmentSourceDocumentTypes",
    component: <FMS_Settings_AllotmentSourceDocumentTypes />,
    permissionTypeId: [ACCESS_RIGHTS.FMS_STATICDATA_ALLOTMENTSOURCEDOCUMENTTYPES],
  },
  {
    path: "/fms/settings/bankDetails",
    component: <FMS_Settings_BankDetails />,
    permissionTypeId: [ACCESS_RIGHTS.FMS_STATICDATA_BANKS, ACCESS_RIGHTS.FMS_STATICDATA_BANKBRANCHES, ACCESS_RIGHTS.FMS_STATICDATA_BANKACCOUNTS],
  },
  {
    path: "/fms/settings/clients",
    component: <FMS_Settings_Clients />,
    permissionTypeId: [ACCESS_RIGHTS.FMS_STATICDATA_CLIENTS],
  },
  {
    path: "/fms/settings/debtors",
    component: <FMS_Settings_Clients />,
    permissionTypeId: [ACCESS_RIGHTS.FMS_STATICDATA_CLIENTS],
  },
  {
    path: "/fms/settings/documenttypes",
    component: <FMS_Settings_DocumentTypes />,
    permissionTypeId: [ACCESS_RIGHTS.FMS_STATICDATA_DOCUMENTTYPES],
  },
  {
    path: "/fms/settings/fundsources",
    component: <FMS_Settings_FundSources />,
    permissionTypeId: [
      ACCESS_RIGHTS.FMS_STATICDATA_AUTHORIZATIONCODES,
      ACCESS_RIGHTS.FMS_STATICDATA_FINANCINGSOURCES,
      ACCESS_RIGHTS.FMS_STATICDATA_FUNDCATEGORIES,
      ACCESS_RIGHTS.FMS_STATICDATA_FUNDCLUSTERS,
    ],
  },
  {
    path: "/fms/settings/chartOfAccounts",
    component: <FMS_Settings_ChartOfAccounts />,
    permissionTypeId: [ACCESS_RIGHTS.FMS_STATICDATA_GENERALLEDGERS, ACCESS_RIGHTS.FMS_STATICDATA_SUBSIDIARYLEDGERS],
  },
  {
    path: "/fms/settings/objectCodes",
    component: <FMS_Settings_ObjectCodes />,
    permissionTypeId: [ACCESS_RIGHTS.FMS_STATICDATA_OBJECTCODES],
  },
  {
    path: "/fms/settings/offices",
    component: <FMS_Settings_Offices />,
    permissionTypeId: [ACCESS_RIGHTS.FMS_STATICDATA_RESPONSIBILITYCENTERS],
  },
  {
    path: "/fms/settings/programs",
    component: <FMS_Settings_Programs />,
    permissionTypeId: [],
  },
  {
    path: "/fms/settings/transactionTypes",
    component: <FMS_Settings_TransactionTypes />,
    permissionTypeId: [ACCESS_RIGHTS.FMS_STATICDATA_TRANSACTIONTYPES],
  },
  {
    path: "/fms/settings/disbursement-transactionTypes",
    component: <FMS_Settings_TransactionTypes />,
    permissionTypeId: [ACCESS_RIGHTS.FMS_STATICDATA_TRANSACTIONTYPES],
  },
  {
    path: "/fms/accounting/depreciationschedules",
    component: <FMS_Accounting_DepreciationSchedules />,
    permissionTypeId: [],
  },

  {
    path: "/fms/accounting/transactioninboxes",
    component: <FMS_Accounting_TransactionInboxes />,
    permissionTypeId: [],
  },
  {
    path: "/fms/budget/appropriations",
    component: <FMS_Transactions_Appropriations />,
    permissionTypeId: [
      ACCESS_RIGHTS.FMS_TRANSACTIONS_APPROPRIATIONS_VIEWER,
      ACCESS_RIGHTS.FMS_TRANSACTIONS_APPROPRIATIONS_REQUESTOR,
      ACCESS_RIGHTS.FMS_TRANSACTIONS_APPROPRIATIONS_APPROVER,
    ],
  },
  {
    path: "/fms/budget/allotments",
    component: <FMS_Transactions_Allotments />,
    permissionTypeId: [
      ACCESS_RIGHTS.FMS_TRANSACTIONS_ALLOTMENTS_VIEWER,
      ACCESS_RIGHTS.FMS_TRANSACTIONS_ALLOTMENTS_REQUESTOR,
      ACCESS_RIGHTS.FMS_TRANSACTIONS_ALLOTMENTS_APPROVER,
    ],
  },
  {
    path: "/fms/budget/bur",
    component: <FMS_Budget_BUR />,
    permissionTypeId: [ACCESS_RIGHTS.FMS_BUDGET_BURS],
  },
  {
    path: "/fms/budget/gaa",
    component: <FMS_Budget_GAA />,
    permissionTypeId: [ACCESS_RIGHTS.FMS_BUDGET_GAAS],
  },
  {
    path: "/fms/budget/saro",
    component: <FMS_Budget_SARO />,
    permissionTypeId: [ACCESS_RIGHTS.FMS_BUDGET_SAROS],
  },
  {
    path: "/fms/transactions/prepayments",
    component: <FMS_Transactions_Prepayments />,
    permissionTypeId: [
      ACCESS_RIGHTS.FMS_TRANSACTIONS_PREPAYMENTS_VIEWER,
      ACCESS_RIGHTS.FMS_TRANSACTIONS_PREPAYMENTS_REQUESTOR,
      ACCESS_RIGHTS.FMS_TRANSACTIONS_PREPAYMENTS_APPROVER,
    ],
  },
  {
    path: "/fms/transactions/cashdisbursements",
    component: <FMS_Transactions_CashDisbursements />,
    permissionTypeId: [
      ACCESS_RIGHTS.FMS_TRANSACTIONS_CASHDISBURSEMENTS_VIEWER,
      ACCESS_RIGHTS.FMS_TRANSACTIONS_CASHDISBURSEMENTS_REQUESTOR,
      ACCESS_RIGHTS.FMS_TRANSACTIONS_CASHDISBURSEMENTS_APPROVER,
    ],
  },
  {
    path: "/fms/transactions/checkdisbursements",
    component: <FMS_Transactions_CheckDisbursements />,
    permissionTypeId: [
      ACCESS_RIGHTS.FMS_TRANSACTIONS_CHECKDISBURSEMENTS_VIEWER,
      ACCESS_RIGHTS.FMS_TRANSACTIONS_CHECKDISBURSEMENTS_REQUESTOR,
      ACCESS_RIGHTS.FMS_TRANSACTIONS_CHECKDISBURSEMENTS_APPROVER,
    ],
  },
  {
    path: "/fms/transactions/collections",
    component: <FMS_Transactions_Collections />,
    permissionTypeId: [
      ACCESS_RIGHTS.FMS_TRANSACTIONS_COLLECTIONS_VIEWER,
      ACCESS_RIGHTS.FMS_TRANSACTIONS_COLLECTIONS_REQUESTOR,
      ACCESS_RIGHTS.FMS_TRANSACTIONS_COLLECTIONS_APPROVER,
    ],
  },
  {
    path: "/fms/transactions/deposits",
    component: <FMS_Transactions_Deposits />,
    permissionTypeId: [
      ACCESS_RIGHTS.FMS_TRANSACTIONS_DEPOSITS_VIEWER,
      ACCESS_RIGHTS.FMS_TRANSACTIONS_DEPOSITS_REQUESTOR,
      ACCESS_RIGHTS.FMS_TRANSACTIONS_DEPOSITS_APPROVER,
    ],
  },
  {
    path: "/fms/transactions/disbursementvouchers",
    component: <FMS_Transactions_DisbursementVouchers />,
    permissionTypeId: [
      ACCESS_RIGHTS.FMS_TRANSACTIONS_DISBURSEMENTVOUCHERS_VIEWER,
      ACCESS_RIGHTS.FMS_TRANSACTIONS_DISBURSEMENTVOUCHERS_REQUESTOR,
      ACCESS_RIGHTS.FMS_TRANSACTIONS_DISBURSEMENTVOUCHERS_REVIEWER,
      ACCESS_RIGHTS.FMS_TRANSACTIONS_DISBURSEMENTVOUCHERS_FUNDER,
      ACCESS_RIGHTS.FMS_TRANSACTIONS_DISBURSEMENTVOUCHERS_APPROVER,
    ],
  },
  {
    path: "/fms/transactions/jev",
    component: <FMS_Transactions_JEV />,
    permissionTypeId: [
      ACCESS_RIGHTS.FMS_TRANSACTIONS_JOURNALENTRYVOUCHERS_VIEWER,
      ACCESS_RIGHTS.FMS_TRANSACTIONS_JOURNALENTRYVOUCHERS_REQUESTOR,
      ACCESS_RIGHTS.FMS_TRANSACTIONS_JOURNALENTRYVOUCHERS_APPROVER,
    ],
  },
  {
    path: "/fms/transactions/obligations",
    component: <FMS_Transactions_Obligations />,
    permissionTypeId: [
      ACCESS_RIGHTS.FMS_TRANSACTIONS_OBLIGATIONS_VIEWER,
      ACCESS_RIGHTS.FMS_TRANSACTIONS_OBLIGATIONS_REQUESTOR,
      ACCESS_RIGHTS.FMS_TRANSACTIONS_OBLIGATIONS_APPROVER,
    ],
  },
  {
    path: "/fms/transactions/orderofpayments",
    component: <FMS_Transactions_OrderOfPayments />,
    permissionTypeId: [
      ACCESS_RIGHTS.FMS_TRANSACTIONS_ORDEROFPAYMENTS_VIEWER,
      ACCESS_RIGHTS.FMS_TRANSACTIONS_ORDEROFPAYMENTS_REQUESTOR,
      ACCESS_RIGHTS.FMS_TRANSACTIONS_ORDEROFPAYMENTS_APPROVER,
    ],
  },
  {
    path: "/fms/reports/accounting",
    component: <FMS_Reports_Accounting />,
    permissionTypeId: [],
  },
  {
    path: "/fms/reports/budget",
    component: <FMS_Reports_Budget />,
    permissionTypeId: [],
  },

  {
    path: "/fms/reports/collection",
    component: <FMS_Reports_Collection />,
    permissionTypeId: [],
  },

  {
    path: "/fms/reports/disbursement",
    component: <FMS_Reports_Disbursement />,
    permissionTypeId: [],
  },
  // #endregion

  { path: "/socials/faq", component: <Socials_FAQ /> },
  { path: "/socials/terms-conditions", component: <Socials_TermsAndConditions />, },
  { path: "/socials/privacy-policy", component: <Socials_PrivacyPolicy /> },

  // this route should be at the end of all other routes
  // eslint-disable-next-line react/display-name
  { path: "/", exact: true, component: <Navigate to="/index" />, },
  { path: "*", component: <Navigate to="/notfound" /> },
];

const publicRoutes = [
  // Authentication Page
  { path: "/logout", component: <Logout /> },
  { path: "/login", component: <AuthPage_LRA /> },
  { path: "/forgot-password", component: <ForgetPassword /> },
  { path: "/register", component: <Register /> },
  { path: "/lockscreen", component: <LockScreen /> },
  { path: "/notfound", component: <NotFound /> },
  { path: "/reset-password", component: <ResetPassword /> },
  { path: "/expired-token", component: <ExpiredToken /> },

  //AuthenticationInner pages
  { path: "/auth-404-basic", component: <Basic404 /> },
  { path: "/auth-404-alt", component: <Alt404 /> },
  { path: "/auth-500", component: <Error500 /> },
  { path: "/pages-maintenance", component: <Maintenance /> },
  { path: "/pages-coming-soon", component: <ComingSoon /> },
  { path: "/auth-offline", component: <Offlinepage /> },
];

export { authProtectedRoutes, publicRoutes };

